import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectPlanningAPI, jobsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  ClipboardList, 
  Calendar, 
  Users, 
  Target,
  ArrowRight,
  Briefcase,
  Clock,
  CheckCircle2,
  AlertCircle,
  LayoutDashboard,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const ProjectPlanning = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    job_id: '',
    title: '',
    description: '',
    start_date: '',
    target_end_date: '',
    budget_allocated: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, jobsRes] = await Promise.all([
        projectPlanningAPI.getPlans(),
        jobsAPI.getMyJobs()
      ]);
      setPlans(plansRes.data.plans || []);
      setJobs(jobsRes.data.jobs || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load project plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (!createForm.title) {
      toast.error('Please enter a project title');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...createForm,
        job_id: createForm.job_id || null,
        budget_allocated: createForm.budget_allocated ? parseFloat(createForm.budget_allocated) : null,
        start_date: createForm.start_date || null,
        target_end_date: createForm.target_end_date || null
      };
      
      const response = await projectPlanningAPI.createPlan(payload);
      toast.success('Project plan created!');
      setShowCreateModal(false);
      setCreateForm({ job_id: '', title: '', description: '', start_date: '', target_end_date: '', budget_allocated: '' });
      fetchData();
      
      // Navigate to the new plan
      if (response.data.plan?._id) {
        navigate(`/project-planning/${response.data.plan._id}`);
      }
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project plan');
    } finally {
      setCreating(false);
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'planning': return 'bg-purple-100 text-purple-700';
      case 'preparation': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-yellow-100 text-yellow-700';
      case 'finishing': return 'bg-orange-100 text-orange-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatPhase = (phase) => {
    return phase?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Planning';
  };

  // Get jobs without plans
  const availableJobs = jobs.filter(job => 
    !plans.some(plan => plan.job_id === (job.id || job._id))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <LayoutDashboard className="h-8 w-8 text-blue-600" />
              Project Planning
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Organize and track your construction projects with tasks, milestones, and team assignments
            </p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="mt-4 md:mt-0 bg-blue-600 hover:bg-blue-700"
            disabled={availableJobs.length === 0}
            data-testid="create-plan-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project Plan
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <ClipboardList className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{plans.length}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Plans</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                  <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {plans.filter(p => p.phase === 'in_progress').length}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {plans.filter(p => p.phase === 'completed').length}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                  <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {plans.reduce((acc, p) => acc + (p.tasks_count || 0), 0)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plans Grid */}
        {plans.length === 0 ? (
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="py-16 text-center">
              <ClipboardList className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Project Plans Yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Create your first project plan to start organizing tasks, tracking milestones, and managing your construction jobs more effectively.
              </p>
              {availableJobs.length > 0 ? (
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Plan
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button 
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="create-standalone-project-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Project
                  </Button>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Projects can stand alone — or link one to a job posting later.
                  </p>
                  <Button 
                    onClick={() => navigate('/post-job')}
                    variant="outline"
                    data-testid="post-job-alt-btn"
                  >
                    <Briefcase className="h-4 w-4 mr-2" />
                    Or Post a Job First
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card 
                key={plan._id} 
                className="hover:shadow-lg transition-shadow cursor-pointer dark:bg-gray-800 dark:border-gray-700"
                onClick={() => navigate(`/project-planning/${plan._id}`)}
                data-testid={`plan-card-${plan._id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg text-gray-900 dark:text-white line-clamp-1">
                        {plan.title}
                      </CardTitle>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                        <Briefcase className="h-3 w-3" />
                        {plan.job_title}
                      </p>
                      {!plan.job_id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/post-job', { state: { fromPlan: { id: plan._id, title: plan.title, description: plan.description || '', budget: plan.budget_allocated } } });
                          }}
                          className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 underline"
                          data-testid={`convert-to-job-btn-${plan._id}`}
                        >
                          <Briefcase className="h-3 w-3" /> Convert to Job
                        </button>
                      )}
                    </div>
                    <Badge className={getPhaseColor(plan.phase)}>
                      {formatPhase(plan.phase)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Progress</span>
                      <span className="font-medium text-gray-900 dark:text-white">{plan.progress || 0}%</span>
                    </div>
                    <Progress value={plan.progress || 0} className="h-2" />
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span>{plan.completed_tasks || 0}/{plan.tasks_count || 0} tasks</span>
                    </div>
                    {plan.target_end_date && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Calendar className="h-4 w-4 text-blue-500" />
                        <span>{new Date(plan.target_end_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  {plan.job_location && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-3 truncate">
                      📍 {plan.job_location}
                    </p>
                  )}

                  {/* View Button */}
                  <Button 
                    variant="ghost" 
                    className="w-full mt-4 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/project-planning/${plan._id}`);
                    }}
                  >
                    View Board
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Plan Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Create Project Plan</h2>
            
            <form onSubmit={handleCreatePlan} className="space-y-4">
              {/* Job Selection (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Link a Job (optional)
                </label>
                <select
                  value={createForm.job_id}
                  onChange={(e) => setCreateForm({ ...createForm, job_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  data-testid="job-select"
                >
                  <option value="">Standalone project (no job)</option>
                  {availableJobs.map((job) => (
                    <option key={job.id || job._id} value={job.id || job._id}>
                      {job.title} - {job.location}
                    </option>
                  ))}
                </select>
                {availableJobs.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    No linkable jobs — this will be a standalone project.
                  </p>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plan Title *
                </label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  placeholder="e.g., Kitchen Renovation Phase 1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                  data-testid="plan-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Brief description of this project plan..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={createForm.start_date}
                    onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Target End Date
                  </label>
                  <input
                    type="date"
                    value={createForm.target_end_date}
                    onChange={(e) => setCreateForm({ ...createForm, target_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Budget Allocated ($)
                </label>
                <input
                  type="number"
                  value={createForm.budget_allocated}
                  onChange={(e) => setCreateForm({ ...createForm, budget_allocated: e.target.value })}
                  placeholder="e.g., 50000"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={creating || !createForm.title}
                  data-testid="submit-plan-btn"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Plan
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPlanning;
